/**
 * @file JWT 내용 읽기 (서명 검증 없음)
 * @description jsonwebtoken의 decode와 같은 결과를 돌려준다
 *              jsonwebtoken은 서명 검증용 암호화 모듈(crypto 폴리필: elliptic, bn.js, browserify-* 등)까지 번들에 들어가서,
 *              브라우저에서는 내용만 읽는 이 함수를 쓴다 (프론트는 decode만 쓰고 검증은 서버에서 함)
 *
 *              jsonwebtoken 8.5 / jws 3 decode 동작
 *              - 헤더.내용.서명 형식이 아니거나 헤더가 JSON이 아니면 null
 *              - 헤더 typ이 JWT면 내용을 JSON.parse (JSON이 아니면 예외)
 *              - 그 외에는 내용이 JSON 객체면 객체, 아니면 문자열
 * @author 백재인
 * @date 2026-09-30
 */

const JWS_REGEX = /^[a-zA-Z0-9\-_]+?\.[a-zA-Z0-9\-_]+?\.([a-zA-Z0-9\-_]+)?$/

// base64url → base64 (padding 포함)
const toBase64 = (value: string) => {
    const base64 = value.replace(/-/g, '+').replace(/_/g, '/')
    return base64 + '='.repeat((4 - (base64.length % 4)) % 4)
}

// 헤더는 jws와 같이 binary(latin1)로 읽는다
const toBinaryString = (value: string) => window.atob(toBase64(value))

// 내용은 utf8로 읽는다
const toUtf8String = (value: string) => {
    const binary = toBinaryString(value)
    const bytes = new Uint8Array(binary.length)
    for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i)

    return new TextDecoder().decode(bytes)
}

const safeJsonParse = (value: string) => {
    try {
        return JSON.parse(value)
    } catch (e) {
        return undefined
    }
}

function decodeJwt(token: any): any {
    if (typeof token !== 'string' || !JWS_REGEX.test(token)) return null

    const [ encodedHeader, encodedPayload ] = token.split('.')

    let header: any
    try {
        header = safeJsonParse(toBinaryString(encodedHeader))
    } catch (e) {
        return null
    }
    if (!header) return null

    let payload: any = toUtf8String(encodedPayload)

    if (header.typ === 'JWT') payload = JSON.parse(payload)

    if (typeof payload === 'string') {
        const obj = safeJsonParse(payload)
        if (obj !== null && typeof obj === 'object') payload = obj
    }

    return payload
}

export default decodeJwt
